import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea,
} from "recharts";
import { Sparkle, TestTube, Pill, AlertTriangle, Flag, Route } from "lucide-react";

const KIND_META = {
  lab:       { icon: TestTube,      label: "Lab result" },
  rx:        { icon: Pill,          label: "Prescription" },
  alert:     { icon: AlertTriangle, label: "Clinical alert" },
  milestone: { icon: Flag,          label: "Care milestone" },
};

function fmtPct(v) { return v == null ? "—" : `${v.toFixed(1)} mL/min/1.73m²`; }

/**
 * The Med Nexus Life Path — a chronological + projected visualization of the
 * patient's CKD journey. Combines lab history, prescriptions, clinical alerts,
 * and care-plan milestones onto a single time axis, with a 10-year eGFR
 * projection so the patient and family can literally see what's ahead.
 */
export default function LifePath() {
  const [data, setData] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    api.get("/lifepath").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    const rows = [];
    for (const h of data.history) rows.push({ date: h.date, actual: h.egfr });
    for (const p of data.projection) {
      // If date already in rows (near current), merge; otherwise add projection row
      const existing = rows.find((r) => r.date === p.date);
      if (existing) existing.projected = p.egfr;
      else rows.push({ date: p.date, projected: p.egfr });
    }
    return rows.sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [data]);

  if (!data) return <div className="p-10 text-[#A3ACA7]" data-testid="lifepath-loading">Loading Life Path…</div>;

  const dialysisYear = data.projection.find((p) => p.egfr <= 15);

  return (
    <div className="space-y-6" data-testid="lifepath-page">
      <header className="border-b border-[#272E2B] pb-4">
        <div className="flex items-center gap-3">
          <Route size={26} className="text-[#4ADE80]" />
          <h2 className="font-heading text-2xl">Life Path</h2>
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#4ADE80]/10 text-[#4ADE80] border border-[#4ADE80]/30">
            10-year Kidney Journey
          </span>
        </div>
        <p className="text-sm text-[#A3ACA7] mt-2 max-w-3xl">
          Every lab, prescription, and clinical milestone plotted on a single timeline —
          overlaid on the 10-year eGFR trajectory. Hover any marker to see the detail.
        </p>
      </header>

      {/* Chart */}
      <div className="ros-card" data-testid="lifepath-chart-card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <div className="ros-label">Patient</div>
            <div className="font-heading text-lg text-white mt-1">{data.patient.name}</div>
            <div className="text-xs text-[#A3ACA7]">CKD {data.patient.ckd_stage} · age {data.patient.age}</div>
          </div>
          <div>
            <div className="ros-label">Today</div>
            <div className="ros-metric text-3xl mt-1" style={{ color: "#4ADE80" }}>
              {data.history.length ? data.history[data.history.length - 1].egfr.toFixed(0) : "—"}
            </div>
            <div className="text-[11px] text-[#A3ACA7]">mL/min/1.73m²</div>
          </div>
          <div>
            <div className="ros-label">Year 10 (projected)</div>
            <div className="ros-metric text-3xl mt-1" style={{ color: "#60A5FA" }}>
              {data.projection.length ? data.projection[data.projection.length - 1].egfr.toFixed(0) : "—"}
            </div>
            <div className="text-[11px] text-[#A3ACA7]">based on recent slope</div>
          </div>
          <div>
            <div className="ros-label">Dialysis window</div>
            <div className="ros-metric text-3xl mt-1"
                 style={{ color: dialysisYear ? "#F87171" : "#4ADE80" }}>
              {dialysisYear ? `Y+${dialysisYear.year_offset}` : "—"}
            </div>
            <div className="text-[11px] text-[#A3ACA7]">
              {dialysisYear ? `projected eGFR ≤ 15 by ${dialysisYear.date}` : "Not projected"}
            </div>
          </div>
        </div>

        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 12, right: 20, bottom: 4, left: -4 }}>
              <defs>
                <linearGradient id="actualG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#4ADE80" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="projG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1C201E" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" stroke="#A3ACA7" fontSize={10} tickLine={false} axisLine={false}
                     minTickGap={40} tickFormatter={(v) => (v || "").slice(0, 7)} />
              <YAxis stroke="#A3ACA7" fontSize={11} tickLine={false} axisLine={false} width={44}
                     domain={[0, 120]} ticks={[0, 30, 60, 90, 120]} />
              <Tooltip
                contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12, color: "#fff" }}
                labelStyle={{ color: "#A3ACA7", fontSize: 11 }}
                formatter={(v, name) => [fmtPct(v), name === "actual" ? "Actual" : "Projected"]}
              />
              <ReferenceArea y1={0} y2={15} fill="#F87171" fillOpacity={0.08}
                             label={{ value: "Dialysis threshold (eGFR 15)", position: "insideBottomRight",
                                      fill: "#F87171", fontSize: 10 }} />
              <ReferenceLine y={60} stroke="#4ADE80" strokeDasharray="4 4"
                             label={{ value: "Healthy zone (60)", position: "insideTopRight", fill: "#4ADE80", fontSize: 10 }} />
              <Area type="monotone" dataKey="actual" stroke="#4ADE80" strokeWidth={3}
                    fill="url(#actualG)" name="Actual" />
              <Area type="monotone" dataKey="projected" stroke="#60A5FA" strokeWidth={2}
                    strokeDasharray="5 5" fill="url(#projG)" name="Projected" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Event timeline */}
      <div className="ros-card" data-testid="lifepath-events">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle size={18} className="text-[#4ADE80]" />
          <div className="font-heading text-white">Milestones along the path</div>
          <span className="text-[10px] text-[#A3ACA7]">{data.events.length} events</span>
        </div>
        <div className="relative pl-8">
          <div className="absolute left-3 top-0 bottom-0 w-px bg-[#272E2B]" />
          <div className="space-y-4">
            {data.events.map((ev, i) => {
              const M = KIND_META[ev.kind] || KIND_META.milestone;
              const Icon = M.icon;
              const active = hovered === i;
              return (
                <div key={i}
                     onMouseEnter={() => setHovered(i)}
                     onMouseLeave={() => setHovered(null)}
                     data-testid={`lifepath-event-${i}`}
                     className="relative group">
                  <div className="absolute -left-8 top-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                       style={{ borderColor: ev.color, background: active ? ev.color : "#0F1312" }}>
                    <Icon size={12} style={{ color: active ? "#0F1312" : ev.color }} />
                  </div>
                  <div className={`rounded-lg px-3 py-2 transition-colors ${active ? "bg-[#1C201E]" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold text-[#A3ACA7]">{ev.date}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                            style={{ color: ev.color, background: `${ev.color}18`, border: `1px solid ${ev.color}44` }}>
                        {M.label}
                      </span>
                      <span className="font-semibold text-white text-sm">{ev.label}</span>
                    </div>
                    {ev.detail && <div className="text-xs text-[#A3ACA7] mt-1 leading-snug">{ev.detail}</div>}
                  </div>
                </div>
              );
            })}
            {data.events.length === 0 && (
              <div className="text-sm text-[#A3ACA7] py-6">No events yet — as labs and prescriptions accumulate they'll appear here.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
